import { useCallback, useRef } from "react";
import { useGlobalState } from "utilities/contexts/use-global-state-context";

interface UseLazyLoadItemArgs {
    onIntersect: (id: number) => void;
    isLoading: boolean;
    hasData: boolean;
}

const useLazyLoadEnhancedContent = (props: UseLazyLoadItemArgs) => {
    const { onIntersect, isLoading, hasData } = props;
    const { globalState } = useGlobalState();

    const showEnhancedContentUserSetting =
        globalState.getUserSettingValue<boolean>("EnhancedContentExpand");
    const observersRef = useRef<Record<number, IntersectionObserver>>({});

    const getObserverRef = useCallback(
        (id: number) => (node: HTMLElement | null) => {
            if (!node) return;

            // Already loaded or loading → do nothing
            if (hasData || isLoading) {
                return;
            }

            if (observersRef.current[id]) {
                observersRef.current[id].disconnect();
            }

            const observer = new IntersectionObserver(
                (entries) => {
                    if (
                        entries.some((e) => e.isIntersecting && !hasData) &&
                        showEnhancedContentUserSetting
                    ) {
                        onIntersect(id);
                    }
                },
                {
                    rootMargin: "500px",
                    threshold: 0.1,
                }
            );

            observer.observe(node);
            observersRef.current[id] = observer;
        },
        [hasData, isLoading, showEnhancedContentUserSetting, onIntersect]
    );

    return { getObserverRef };
};

export default useLazyLoadEnhancedContent;
